import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";

import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  hint?: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * Editorial multiselect. Trigger looks like an Input — selected items
 * render as removable chips inside. Click opens a custom searchable
 * popover with ↑ ↓ Enter Esc keyboard support.
 */
export const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = "Выбери…",
      emptyText = "Ничего не найдено",
      searchPlaceholder = "Поиск…",
      disabled,
      className,
      id,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [activeIndex, setActiveIndex] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const listRef = React.useRef<HTMLDivElement | null>(null);

    const valueSet = React.useMemo(() => new Set(value), [value]);

    const filtered = React.useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return options;
      return options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q) ||
          (o.hint?.toLowerCase().includes(q) ?? false),
      );
    }, [options, query]);

    // Reset active index whenever the filter changes
    React.useEffect(() => {
      setActiveIndex(0);
    }, [query, open]);

    // Close on outside click
    React.useEffect(() => {
      if (!open) return;
      function onDown(e: MouseEvent) {
        if (!containerRef.current) return;
        if (!containerRef.current.contains(e.target as Node)) {
          setOpen(false);
          setQuery("");
        }
      }
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    // Focus search when opening
    React.useEffect(() => {
      if (open) {
        const t = setTimeout(() => inputRef.current?.focus(), 0);
        return () => clearTimeout(t);
      }
    }, [open]);

    // Selected options preserve declared option order (stable chip layout)
    const selected = React.useMemo(
      () => options.filter((o) => valueSet.has(o.value)),
      [options, valueSet],
    );

    function toggle(v: string) {
      if (valueSet.has(v)) onChange(value.filter((x) => x !== v));
      else onChange([...value, v]);
    }

    function removeChip(e: React.MouseEvent, v: string) {
      e.stopPropagation();
      onChange(value.filter((x) => x !== v));
    }

    function onKeyDown(e: React.KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        scrollIntoView(listRef.current, activeIndex + 1);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        scrollIntoView(listRef.current, activeIndex - 1);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const opt = filtered[activeIndex];
        if (opt) toggle(opt.value);
      }
      if (e.key === "Backspace" && query === "" && value.length > 0) {
        // Pop the last chip on backspace in empty search
        onChange(value.slice(0, -1));
      }
    }

    return (
      <div ref={mergeRefs(ref, containerRef)} className={cn("relative", className)}>
        {/* Trigger */}
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            "flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-zinc-800 bg-zinc-950/40 px-2 py-1 text-left text-sm",
            "shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors",
            "hover:border-zinc-700",
            open && "border-zinc-600 ring-1 ring-zinc-600",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          {selected.length === 0 ? (
            <span className="px-1 text-zinc-500">{placeholder}</span>
          ) : (
            selected.map((opt) => (
              <span
                key={opt.value}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 px-1.5 py-0.5",
                  "text-[12px] text-zinc-200",
                )}
              >
                {opt.label}
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => removeChip(e, opt.value)}
                  className="grid h-4 w-4 place-items-center rounded-sm text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
                  aria-label={`Удалить ${opt.label}`}
                >
                  <X className="h-3 w-3" />
                </span>
              </span>
            ))
          )}
          <span className="ml-auto pl-1 text-zinc-500">
            <ChevronDown className="h-4 w-4" />
          </span>
        </button>

        {/* Popover */}
        {open && (
          <div
            className={cn(
              "absolute left-0 right-0 top-full z-50 mt-1 rounded-md border border-zinc-800 bg-[#121215]",
              "shadow-[0_12px_32px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.04)]",
            )}
          >
            <div className="border-b border-zinc-800/80 p-2">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                className={cn(
                  "h-8 w-full rounded-sm border-0 bg-transparent px-1 text-sm text-zinc-100",
                  "placeholder:text-zinc-500 focus:outline-none",
                )}
              />
            </div>
            <div
              ref={listRef}
              className="max-h-64 overflow-y-auto p-1"
              role="listbox"
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-zinc-500">
                  {emptyText}
                </div>
              ) : (
                filtered.map((opt, i) => {
                  const isSelected = valueSet.has(opt.value);
                  const isActive = i === activeIndex;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => toggle(opt.value)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                        isActive ? "bg-zinc-800/80 text-zinc-50" : "text-zinc-200",
                      )}
                    >
                      <span
                        className={cn(
                          "grid h-4 w-4 place-items-center rounded-sm border",
                          isSelected
                            ? "border-[#ff5c35] bg-[#ff5c35] text-white"
                            : "border-zinc-700 bg-transparent",
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </span>
                      <span className="flex-1 truncate">{opt.label}</span>
                      {opt.hint && (
                        <span className="font-mono text-[11px] text-zinc-500">
                          {opt.hint}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);
MultiSelect.displayName = "MultiSelect";

function scrollIntoView(list: HTMLDivElement | null, idx: number) {
  if (!list) return;
  const el = list.children[idx] as HTMLElement | undefined;
  if (!el) return;
  const top = el.offsetTop;
  const bottom = top + el.offsetHeight;
  if (top < list.scrollTop) list.scrollTop = top;
  else if (bottom > list.scrollTop + list.clientHeight)
    list.scrollTop = bottom - list.clientHeight;
}

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (node) => {
    for (const r of refs) {
      if (!r) continue;
      if (typeof r === "function") r(node);
      else (r as React.MutableRefObject<T | null>).current = node;
    }
  };
}
